/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useState } from 'react'
import { Form as FinalForm } from 'react-final-form'
import { FieldArray } from 'react-final-form-arrays'
import arrayMutators from 'final-form-arrays'
import createDecorator from 'final-form-focus'
import Typography from '@mui/material/Typography'
import { Revision, Revisions } from 'components/Revisions'
import DetailsBlock from 'components/DetailsBlock'
import { CourseName } from 'theme/utils'
import { validateRequired, validateRequiredDurationInMinutes, validateRequiredWPM } from 'utils/validation'
import { CreateQuestionPassageBankInput } from '__generatedTypes__/globalTypes'
import {
  findOneQuestionPassageBank_findOneQuestionPassageBank as _QuestionPassageBank,
  findOneQuestionPassageBank_findOneQuestionPassageBank_questionPassages as QuestionPassage,
  findOneQuestionPassageBank_findOneQuestionPassageBank_subject as Subject,
} from '__generatedTypes__/findOneQuestionPassageBank'
import { useQuestionsSubmitError } from 'hooks/useQuestionsSubmitError'
import { toast } from 'utils/toast'
import { SelectField } from 'components/Select'
import {
  CARS_SPEED_READING_GAME,
  CARS_VOCAB_IDENTIFIER_GAME,
  LEVEL_DIFFICULTY,
  LevelOption,
  MAIN_IDEA_GAME,
} from 'pagesNew/QuestionPassages/Form/consts'
import * as commonClasses from 'pagesNew/Video/commonStyles'
import { QuestionPassagePicker } from 'components/QuestionPassagePicker'
import QuestionPassageAccordion from 'components/QuestionPassageAccordion'
import { InputField, NumberInputField } from 'components/Input'
import { durationToNumber, durationToString } from '../../PracticeTests/Form'

export const getMinPassagesCount = (gameSlug: string) => {
  if (gameSlug === CARS_VOCAB_IDENTIFIER_GAME) {
    return 3
  }
  if (gameSlug === MAIN_IDEA_GAME) {
    return 3
  }
  return 2
}

export type QuestionPassageBank = Omit<_QuestionPassageBank, 'level' | 'passageTime'> & {
  level: LevelOption
  passageTime: number | string // in seconds
}

export type Props = {
  questionPassageBank: QuestionPassageBank | null
  subject: Subject
  level: LevelOption | undefined
  availableLevels: LevelOption[]
  onSubmit: (questionPassageBank: CreateQuestionPassageBankInput) => Promise<void>
  onDelete?: () => Promise<void>
}

const focusOnErrors = createDecorator<QuestionPassageBank, Partial<QuestionPassageBank>>()

export const prepareForSubmit = (values: Partial<QuestionPassageBank>): CreateQuestionPassageBankInput => {
  const levelNumber = LEVEL_DIFFICULTY.findIndex((l) => l.id === values.level?.id) + 1
  return {
    subject: { id: values.subject?.id ?? '' },
    history: values.history,
    isLive: values.isLive,
    level: values.level?.id,
    order: levelNumber,
    questionPassagesIds: values.questionPassages?.map(({ id }) => id),
    questionPassages: values.questionPassages?.map(({ id }) => ({ id })),
    title: `Level ${levelNumber}`,
    passageTime: values.passageTime ? durationToNumber(String(values.passageTime)) : 0,
    wpm: Number(values.wpm ?? 0),
  }
}

export const getInitialValues = (
  questionPassageBank: QuestionPassageBank | null,
  subject: Subject,
  level: LevelOption | undefined
): Partial<QuestionPassageBank> => {
  if (!questionPassageBank) {
    return {
      subject,
      history: [],
      isLive: false,
      level,
      order: 0,
      questionPassagesIds: [],
      questionPassages: [],
      title: '',
      passageTime: '',
      wpm: undefined,
    }
  }

  return {
    ...questionPassageBank,
    passageTime: durationToString(Number(questionPassageBank.passageTime ?? 0)),
    questionPassages: (questionPassageBank.questionPassagesIds
      .map((questionPassageId) => questionPassageBank.questionPassages.find(({ id }) => id === questionPassageId))
      .filter((questionPassage) => !!questionPassage) ?? []) as QuestionPassageBank['questionPassages'],
  }
}

export const Form = ({ questionPassageBank, subject, level, availableLevels, onSubmit, onDelete }: Props) => {
  const [submitting, setSubmitting] = useState(false)
  const [initialValues, setInitialValues] = useState(getInitialValues(questionPassageBank, subject, level))
  const { submitQuestionError, setSubmitQuestionError, handleServerError } = useQuestionsSubmitError()

  useEffect(() => {
    setInitialValues(getInitialValues(questionPassageBank, subject, level))
  }, [questionPassageBank?.id])

  const handleSubmit = useCallback(
    async (values: QuestionPassageBank) => {
      try {
        const questionPassageBankData = prepareForSubmit(values)

        await onSubmit(questionPassageBankData)
        setSubmitQuestionError(null)
      } catch (e) {
        toast.error('Failed to prepare data for submit')
        console.error(e)
        handleServerError(e)
      }
    },
    [onSubmit]
  )

  const handleDelete = useCallback(async () => {
    setSubmitting(true)
    try {
      await onDelete?.()
    } catch (e) {
      console.error(e)
    }
    setSubmitting(false)
  }, [onDelete])

  return (
    <div css={commonClasses.container} data-testid="qb-form-wrapper">
      <Typography variant="title3" css={commonClasses.title} data-testid="qb-title">
        {initialValues.id ? 'Edit' : 'Create'} CARS Question Bank for {subject.tabName}
      </Typography>
      <FinalForm<QuestionPassageBank>
        mutators={{ ...arrayMutators }}
        decorators={[focusOnErrors]}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, initialValues, submitting: formSubmitting, form, values }) => (
          <form onSubmit={handleSubmit} css={commonClasses.contentContainer} data-testid="qb-form">
            <div css={commonClasses.block}>
              <SelectField
                name="level"
                validate={validateRequired}
                items={availableLevels}
                label="Level Difficulty"
                placeholder="Choose Level"
                onChange={(selectedLevel) => {
                  if (selectedLevel) {
                    form.change(
                      'questionPassages',
                      values.questionPassages.filter(({ level }) => !level.includes(selectedLevel?.id))
                    )
                  }
                }}
                disabled={!!questionPassageBank}
                displaySelectedItemsFirst
                fullWidth
              />
              <InputField
                name="passageTime"
                validate={validateRequiredDurationInMinutes}
                label="Passage Time"
                placeholder="Enter Passage Time (MM:SS)"
                fullWidth
              />
              {subject.slug === CARS_SPEED_READING_GAME && (
                <NumberInputField
                  name="wpm"
                  validate={validateRequiredWPM}
                  label="WPM"
                  placeholder="Enter WPM"
                  fullWidth
                />
              )}
              <FieldArray<QuestionPassage>
                name="questionPassages"
                validate={(value) => {
                  const requiredError = validateRequired(value)
                  const minCount = getMinPassagesCount(subject.slug)
                  return (
                    requiredError || (+value?.length < minCount && `Must contain at least ${minCount} CARS Questions`)
                  )
                }}
              >
                {({ fields, meta }) => (
                  <>
                    <QuestionPassagePicker
                      label="CARS Questions"
                      value={fields.value ?? []}
                      onChange={(questionPassages) => form.change('questionPassages', questionPassages)}
                      error={meta.touched && meta.error}
                      formHelperText={meta.touched && typeof meta.error === 'string' ? meta.error : undefined}
                      filter={{ subject: { id: subject.id } }}
                      hideSelectedItems
                      fullWidth
                    />
                    <QuestionPassageAccordion
                      questionPassages={fields.value ?? []}
                      onSort={(questionPassages) => form.change('questionPassages', questionPassages)}
                      onDeleteClick={(questionPassage, index) => fields.remove(index)}
                      onQuestionPassageUpdated={(questionPassageUpd, index) => fields.update(index, questionPassageUpd)}
                    />
                  </>
                )}
              </FieldArray>
              {!!initialValues.history?.length && (
                <Revisions revisions={(initialValues?.history ?? []) as Revision[]} />
              )}
            </div>
            <DetailsBlock
              title="Test Details"
              itemName="CARS Question Bank"
              tag={CourseName.CARS}
              tagLabel={subject.tabName}
              createDate={initialValues?.createdAt}
              statusLive={!!initialValues?.isLive}
              onDeleteClick={handleDelete}
              disabled={formSubmitting || submitting}
              deleteDisabledReason={
                initialValues.isLive ? `You can't delete CARS Question Bank because Live is enabled` : undefined
              }
              errorText={submitQuestionError}
            />
          </form>
        )}
      />
    </div>
  )
}
